import { useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { Position } from '../../../services/careers/types';
import { InventorySlideOver } from '../../ui/InventorySlideOver';
import ApplicationForm from './ApplicationForm';

export default function ApplicationDrawer({ position, isOpen, onClose, onPosition }: {
  position: Position; isOpen: boolean; onClose: () => void; onPosition: (position: Position) => void;
}) {
  const { t } = useTranslation(undefined, { lng: 'en' });
  const dialog = useRef<HTMLDivElement>(null);
  const close = useRef(onClose); close.current = onClose;
  useLayoutEffect(() => {
    const node = dialog.current;
    if (!node) return;
    if (!isOpen) { node.setAttribute('inert', ''); return; }
    node.removeAttribute('inert');
    const trigger = document.getElementById('careers-apply-button');
    const scroller = document.getElementById('mainContainer');
    const previousOverflow = scroller?.style.overflow;
    if (scroller) scroller.style.overflow = 'hidden';
    const siblings = Array.from(document.body.children).filter(element => element !== node);
    const previousInert = siblings.map(element => element.hasAttribute('inert'));
    siblings.forEach(element => element.setAttribute('inert', ''));
    if (!node.contains(document.activeElement)) node.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); close.current(); }
      if (event.key !== 'Tab') return;
      const controls = Array.from(node.querySelectorAll<HTMLElement>('button, a[href], input, select, textarea, [tabindex="0"]'))
        .filter(element => !element.matches(':disabled') && !element.closest('[hidden]') && element.tabIndex >= 0);
      const first = controls[0]; const last = controls[controls.length - 1];
      if (!first) { event.preventDefault(); node.focus(); }
      else if (event.shiftKey && (document.activeElement === first || document.activeElement === node)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || document.activeElement === node)) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      siblings.forEach((element, index) => { if (!previousInert[index]) element.removeAttribute('inert'); });
      if (scroller) scroller.style.overflow = previousOverflow ?? '';
      trigger?.focus({ preventScroll: true });
    };
  }, [isOpen]);
  return createPortal(<div ref={dialog} id="careers-application-dialog" lang="en" role={isOpen ? 'dialog' : undefined}
    aria-modal={isOpen || undefined} aria-label={t('hiring.apply')} aria-hidden={!isOpen} tabIndex={-1}
    className="ph-no-capture ph-mask text-primary dark:text-primary-dark">
    <InventorySlideOver isOpen={isOpen} onClose={onClose} title={t('hiring.apply')} subtitle={position.content.en.title}
      closeLabel={t('common.close')} widthClassName="max-w-none lg:max-w-[60vw]" zIndexClass="z-[70]" footer={null}>
      {isOpen && <div className="mx-auto mt-6 w-full max-w-3xl pb-8 sm:mt-8"><ApplicationForm position={position} onPosition={onPosition} /></div>}
    </InventorySlideOver>
  </div>, document.body);
}
